"use client";

// Sonner shim: the codebase mounts <Toaster /> from sonner (providers.tsx),
// but ~16 call sites still import this hook expecting the shadcn/ui toast API.
// This file forwards those calls to sonner so they actually render.

import { toast as sonnerToast } from "sonner";

type Variant = "default" | "destructive" | "success" | "warning";

type ToastInput = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: Variant;
};

function emit(props: ToastInput): string | number {
  const { title, description, variant } = props;
  const message = (title ?? "") as string;
  const opts = description ? { description: description as string } : undefined;

  switch (variant) {
    case "destructive":
      return sonnerToast.error(message, opts);
    case "warning":
      return sonnerToast.warning(message, opts);
    case "success":
    case "default":
    case undefined:
    default:
      return sonnerToast.success(message, opts);
  }
}

function toast(props: ToastInput) {
  const id = emit(props);
  return {
    id: String(id),
    dismiss: () => sonnerToast.dismiss(id),
    update: (next: ToastInput) => {
      sonnerToast.dismiss(id);
      emit(next);
    },
  };
}

function useToast() {
  return {
    toast,
    toasts: [] as never[],
    dismiss: (id?: string) => {
      if (id) sonnerToast.dismiss(id);
      else sonnerToast.dismiss();
    },
  };
}

export { useToast, toast };
